// src/components/CardsGraficos.jsx
// Painel do Diário de Obra (RDO) — substitui o antigo dashboard de cards + RDO.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../supabaseClient";
import "./CardsGraficos.css";

/* ════════════════════════════════════════════════════════════════════
   Constantes e helpers
   ════════════════════════════════════════════════════════════════════ */
const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const DIAS_CURTO = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const DIAS_LONGO = ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"];

const STATUS_INFO = {
  op: { label: "Operacional", cls: "op" },
  parcial: { label: "Parcialmente operacional", cls: "parcial" },
  parado: { label: "Obra parada", cls: "parado" },
  vazio: { label: "Sem status operacional", cls: "vazio" },
  feriado: { label: "Feriado", cls: "feriado" },
  compensado: { label: "Dia compensado", cls: "compensado" },
  pendente: { label: "Pendente", cls: "pendente" },
  hoje: { label: "Hoje", cls: "hoje" },
  fds: { label: "Fim de semana", cls: "fds" },
  futuro: { label: "Futuro", cls: "futuro" },
};

const pad = (n) => String(n).padStart(2, "0");
const toISO = (a, m, d) => `${a}-${pad(m + 1)}-${pad(d)}`;
const num = (v) => Number(v) || 0;
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);
const norm = (s) => String(s || "").trim().toUpperCase();
const txt = (s) => String(s || "").trim();
const fmtBR = (iso) => {
  if (!iso) return "—";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
};
const fmtCurto = (iso) => {
  if (!iso) return "—";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
};
const dowDe = (iso) => {
  const [a, m, d] = iso.split("-").map(Number);
  return new Date(a, m - 1, d).getDay();
};
const mesRange = (ano, mes) => ({
  inicio: toISO(ano, mes, 1),
  fim: toISO(ano, mes, new Date(ano, mes + 1, 0).getDate()),
});

// ── Helpers de data e texto ─────────────────────────────────────────
const addDias = (iso, n) => {
  const [a, m, d] = iso.split("-").map(Number);
  const x = new Date(a, m - 1, d + n);
  return toISO(x.getFullYear(), x.getMonth(), x.getDate());
};
const cortar = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const segundaDe = (iso) => addDias(iso, -((dowDe(iso) + 6) % 7));

// ── Regras do RDO ───────────────────────────────────────────────────
const especial = (r) => !!(r?.feriado || r?.dia_compensado);
const preenchido = (r) =>
  !!r &&
  (Object.values(r.atividades || {}).some((v) => txt(v)) || !!txt(r.responsavel_preenchimento));

const statusOp = (r) => {
  const s = [r.obra_op_manha, r.obra_op_tarde];
  const sims = s.filter((x) => x === "sim").length;
  const naos = s.filter((x) => x === "nao").length;
  if (sims === 2) return "op";
  if (sims === 1) return "parcial";
  if (naos > 0) return "parado";
  return "vazio";
};

const efetivoDia = (r) => {
  let pp = 0, pt = 0, tp = 0, tt = 0;
  (r.efetivo_proprio || []).forEach((e) => {
    if (!txt(e.funcao)) return;
    pp += num(e.presentes);
    pt += num(e.total);
  });
  (r.efetivo_terceirizado || []).forEach((e) => {
    if (!txt(e.funcao)) return;
    tp += num(e.presentes);
    tt += num(e.total);
  });
  return { pp, pt, tp, tt, presentes: pp + tp, total: pt + tt };
};

const ehChuva = (c) =>
  ["09", "10", "11"].includes(String(c.icone || "").slice(0, 2)) ||
  /chuv|garoa|tempest|trovoad|rain|storm|drizzle/i.test(c.condicao || "");

const topicosDoDia = (rdo, pav) => {
  const t = rdo.atividades_topicos?.[pav];
  if (Array.isArray(t) && t.length) return t.filter((x) => txt(x.texto));
  return String(rdo.atividades?.[pav] || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((texto) => ({ texto, empresa: "" }));
};

const resumir = (rdos) => {
  const validos = rdos.filter((r) => !especial(r) && preenchido(r));
  let op = 0, presSoma = 0, diasEf = 0, aus = 0, tot = 0;
  validos.forEach((r) => {
    const s = statusOp(r);
    if (s === "op" || s === "parcial") op++;
    const e = efetivoDia(r);
    if (e.presentes > 0) { presSoma += e.presentes; diasEf++; }
    tot += e.total;
    aus += Math.max(0, e.total - e.presentes);
  });
  return {
    dias: validos.length,
    op,
    pctOp: pct(op, validos.length),
    efetivoMedio: diasEf ? Math.round(presSoma / diasEf) : 0,
    absenteismo: pct(aus, tot),
    interc: validos.filter((r) => r.tem_intercorrencias && txt(r.intercorrencias)).length,
  };
};

/* ════════════════════════════════════════════════════════════════════
   Componentes pequenos
   ════════════════════════════════════════════════════════════════════ */
// ── Torre da obra: cores e status dos pavimentos ─────────────────────
const PALETA_EMPRESAS = ["#e4572e", "#17a398", "#8d5fd3", "#d6409f", "#3a86ff", "#ff8c42", "#4c9f38", "#b08900", "#00a6a6", "#9c2f4b", "#6c8ebf", "#7d6b57"];
const COR_SEM_EMPRESA = "#98a4b3";
const ehTerreo = (nome) => /^(pav\.?\s*|pavimento\s*)?t[eé]rreo$/i.test(txt(nome));

function TagEmpresa({ nome, cores }) {
  const cor = (cores && cores[norm(nome)]) || COR_SEM_EMPRESA;
  return <em className="gd-tag gd-tag--emp" style={{ "--c": cor }}>{nome}</em>;
}

const Icon = ({ name, size = 18, className = "" }) => (
  <span className={`material-symbols-outlined ${className}`} style={{ fontSize: size }}>{name}</span>
);

function Delta({ atual, anterior, inverso = false, unidade = "" }) {
  if (anterior == null) return null;
  const d = atual - anterior;
  if (d === 0) return <span className="gd-delta gd-delta--neutro">igual ao mês anterior</span>;
  const bom = inverso ? d < 0 : d > 0;
  return (
    <span className={`gd-delta ${bom ? "gd-delta--bom" : "gd-delta--ruim"}`}>
      {d > 0 ? "▲" : "▼"} {Math.abs(d)}{unidade} vs. mês anterior
    </span>
  );
}

function Kpi({ icon, label, value, sub, delta, tom = "neutro", visual = null }) {
  return (
    <div className={`gd-kpi gd-kpi--${tom}`}>
      {visual || <div className="gd-kpi__icone"><Icon name={icon} size={20} /></div>}
      <div className="gd-kpi__corpo">
        <div className="gd-kpi__valor">{value}</div>
        <div className="gd-kpi__rotulo">{label}</div>
        {sub && <div className="gd-kpi__sub">{sub}</div>}
        {delta}
      </div>
    </div>
  );
}

function Card({ titulo, icone, badge, className = "", children }) {
  return (
    <section className={`gd-card ${className}`}>
      <header className="gd-card__head">
        <h3>{icone && <Icon name={icone} size={17} />}{titulo}</h3>
        {badge != null && <span className="gd-badge">{badge}</span>}
      </header>
      {children}
    </section>
  );
}

const Vazio = ({ children }) => <p className="gd-vazio">{children}</p>;

function Barra({ valor, max, tom = "azul" }) {
  return (
    <div className="gd-barra">
      <div className={`gd-barra__fill gd-barra__fill--${tom}`} style={{ width: `${Math.min(100, pct(valor, max))}%` }} />
    </div>
  );
}

const tomPresenca = (p) => (p == null ? "azul" : p >= 85 ? "verde" : p >= 65 ? "ambar" : "vermelho");

/* ── Torre da obra (desenho) ─────────────────────────────────────── */
function TorreSvg({ pavs, locs, foco, onPick, onPickEmp, onFotos, onEvolucao }) {
  const PAV_H = 14, STEP = 16;
  const TELHADO_TOP = 4, TELHADO_H = 16, Y0 = TELHADO_TOP + TELHADO_H + 8;
  const LOC_Y = Y0 + pavs.length * STEP + 26;
  const altura = LOC_Y + (locs.length ? 12 + locs.length * STEP : 0) + 6;
  const terreoIdx = pavs.findIndex((p) => ehTerreo(p.nome));

  // Miniaturas de fotos (coluna à esquerda da torre)
  const THUMB = 14, THUMB_GAP = 2, THUMBS_MAX = 3;
  const LEFT = THUMBS_MAX * (THUMB + THUMB_GAP) + 6;

  // Empresas (à direita da torre) — todas aparecem, uma depois da outra
  const EMP_X = 120; // abre espaço para o ícone do carrossel
  const MAX_NOME = 18;
  const larguraEmp = (rotulo) => 12 + rotulo.length * 4.7;
  const larguraLinha = (p) =>
    p.empresas.reduce((a, e) => a + larguraEmp(cortar(e.nome, MAX_NOME)), 0) +
    (p.total > 0 && p.empresas.length === 0 ? 40 : 0);
  const maxLinha = Math.max(0, ...[...pavs, ...locs].map(larguraLinha));
  const W = Math.max(330, Math.ceil(LEFT + EMP_X + maxLinha + 8));

  const tem = (p) => !foco || p.empresas.some((e) => e.nome === foco);
  const titulo = (p) => `${p.nome} — ${p.empresas.map((e) => e.nome).join(", ") || (p.total ? "sem empresa informada" : "sem atividade no dia")}`;
  const tecla = (p) => (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(p.nome); } };

  const linha = (p, y, isT) => {
    const ativo = p.total > 0;
    const cor = isT ? (ativo ? "#22c55e" : "#a7f3d0") : p.cor;
    const ty = y + PAV_H / 2 + 2.3;
    let cx = EMP_X;
    return (
      <g key={p.nome} className="gd-torre__pav" opacity={tem(p) ? 1 : 0.25} tabIndex={0} role="button" aria-label={titulo(p)} onClick={() => onPick(p.nome)} onKeyDown={tecla(p)}>
        <title>{titulo(p)}</title>
        <rect x="6" y={y} width="92" height={PAV_H} rx="1.5" fill={cor} opacity={foco ? 0.55 : 0.92} stroke={isT ? "#020a52" : "none"} strokeWidth={isT ? 1.5 : 0} />
        <text x="10" y={ty} fontSize="6.5" fontWeight="600" fill="#14202e" style={{ pointerEvents: "none" }}>{cortar(p.nome, 20)}</text>

        {/* Ícone do carrossel de evolução — encostado na barra do pavimento */}
        {p.evolucao?.length > 0 && (
          <g
            className="gd-torre__evo"
            tabIndex={0}
            role="button"
            aria-label={`Evolução fotográfica de ${p.nome}`}
            style={{ cursor: "pointer" }}
            onClick={(ev) => { ev.stopPropagation(); onEvolucao(p.nome); }}
            onKeyDown={(ev) => {
              if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); ev.stopPropagation(); onEvolucao(p.nome); }
            }}
          >
            <title>{`Evolução — ${p.evolucao.length} foto${p.evolucao.length > 1 ? "s" : ""}`}</title>
            <rect x="101" y={y + 1} width="13" height={PAV_H - 2} rx="2" fill="#fff" stroke="#020a52" strokeOpacity="0.4" strokeWidth="0.6" />
            <rect x="103.5" y={y + 4} width="8" height="6" rx="1" fill="none" stroke="#020a52" strokeWidth="0.8" />
            <circle cx="107.5" cy={y + 7} r="1.4" fill="#020a52" />
          </g>
        )}

        {p.empresas.map((e) => {
          const rotulo = cortar(e.nome, MAX_NOME);
          const w = larguraEmp(rotulo);
          const x = cx;
          cx += w;
          const sel = foco === e.nome;
          const apagada = !!foco && !sel;
          return (
            <g
              key={e.nome}
              className="gd-torre__emp"
              tabIndex={0}
              role="button"
              aria-label={`${e.nome} — ver serviços`}
              aria-pressed={sel}
              opacity={apagada ? 0.3 : 1}
              style={{ cursor: "pointer" }}
              onClick={(ev) => { ev.stopPropagation(); onPickEmp(e.nome); }}
              onKeyDown={(ev) => {
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault();
                  ev.stopPropagation();
                  onPickEmp(e.nome);
                }
              }}
            >
              <title>{`${e.nome} — ver serviços`}</title>
              {sel && <rect x={x - 2} y={y} width={w - 2} height={PAV_H} rx="3" fill={e.cor} opacity="0.18" stroke={e.cor} strokeWidth="0.8" />}
              <circle cx={x + 3} cy={y + PAV_H / 2} r={sel ? 3.4 : 2.8} fill={e.cor} />
              <text x={x + 8} y={ty} fontSize="6.5" fontWeight={sel ? 700 : 600} fill="#1d2a38">{rotulo}</text>
            </g>
          );
        })}
        {ativo && p.empresas.length === 0 && <text x={EMP_X} y={ty} fontSize="6.5" fill="#98a4b3" style={{ pointerEvents: "none" }}>sem empresa</text>}
      </g>
    );
  };

  // Miniaturas das fotos do pavimento no dia — alinhadas à direita, encostadas na barra do pavimento.
  const miniaturas = (p, y) => {
    const fotos = p.fotos || [];
    if (!fotos.length) return null;
    const vis = fotos.slice(0, THUMBS_MAX);
    const restante = fotos.length - vis.length;
    return (
      <g key={`fotos-${p.nome}`} opacity={tem(p) ? 1 : 0.25}>
        {vis.map((f, k) => {
          const x = LEFT + 4 - (vis.length - k) * (THUMB + THUMB_GAP) + THUMB_GAP;
          const ultimo = k === vis.length - 1 && restante > 0;
          const abrir = () => onFotos(fotos, k);
          return (
            <g
              key={`${f.url}-${k}`}
              className="gd-torre__foto"
              tabIndex={0}
              role="button"
              aria-label={`Ampliar foto ${k + 1} de ${p.nome}`}
              style={{ cursor: "zoom-in" }}
              onClick={abrir}
              onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); abrir(); } }}
            >
              <title>{`${p.nome} — ${fotos.length} foto${fotos.length > 1 ? "s" : ""} (clique para ampliar)`}</title>
              <image href={f.url} x={x} y={y} width={THUMB} height={THUMB} preserveAspectRatio="xMidYMid slice" />
              <rect x={x} y={y} width={THUMB} height={THUMB} fill="none" stroke="#020a52" strokeOpacity="0.35" strokeWidth="0.6" />
              {ultimo && (
                <>
                  <rect x={x} y={y} width={THUMB} height={THUMB} fill="#0f172a" opacity="0.6" />
                  <text x={x + THUMB / 2} y={y + THUMB / 2 + 2.3} textAnchor="middle" fontSize="6" fontWeight="700" fill="#fff" style={{ pointerEvents: "none" }}>+{restante + 1}</text>
                </>
              )}
            </g>
          );
        })}
      </g>
    );
  };

  const yPav = (i) => Y0 + i * STEP;
  const yLoc = (i) => LOC_Y + 8 + i * STEP;

  return (
    <svg viewBox={`0 0 ${W} ${altura}`} xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Torre da obra com os pavimentos, as fotos e as empresas atuando no dia">
      <g transform={`translate(${LEFT},0)`}>
        <polygon points={`52,${TELHADO_TOP} 100,${TELHADO_TOP + TELHADO_H} 4,${TELHADO_TOP + TELHADO_H}`} fill="#94a3b8" />
        <rect x="4" y={TELHADO_TOP + TELHADO_H} width="96" height="5" rx="1" fill="#cbd5e1" />

        {pavs.map((p, i) => linha(p, yPav(i), i === terreoIdx))}

        <rect x="2" y={Y0 + pavs.length * STEP + 2} width="100" height="7" rx="1" fill="#94a3b8" opacity="0.6" />

        {locs.length > 0 && (
          <>
            <line x1="6" y1={LOC_Y - 8} x2={W - LEFT - 6} y2={LOC_Y - 8} stroke="#d0d8e8" strokeWidth="1" strokeDasharray="4,4" />
            <text x="6" y={LOC_Y + 2} fontSize="6.5" fontWeight="700" fill="#8896b3">Locações</text>
            {locs.map((l, i) => linha(l, yLoc(i), false))}
          </>
        )}
      </g>

      {terreoIdx >= 0 && <line x1="0" y1={yPav(terreoIdx) + PAV_H + 1} x2={W} y2={yPav(terreoIdx) + PAV_H + 1} stroke="#020a52" strokeWidth="2" />}

      {pavs.map((p, i) => miniaturas(p, yPav(i)))}
      {locs.map((l, i) => miniaturas(l, yLoc(i)))}
    </svg>
  );
}

/* ── Torre da obra (controles) ───────────────────────────────────── */
function TorreObra({ torre, dia, setDia, hojeISO, foco, setFoco, onPick, onPickEmp, onFotos, onEvolucao, pavSel, empSel, onFechar, cores }) {
  const { pavs, locs, legenda } = torre;
  const seg = segundaDe(dia);
  const semana = Array.from({ length: 7 }, (_, i) => addDias(seg, i));
  const mesDia = Number(dia.split("-")[1]) - 1;
  const proxSemanaBloqueada = addDias(seg, 7) > hojeISO;
  const irSemana = (n) => {
    const novo = addDias(dia, n);
    setDia(novo > hojeISO ? hojeISO : novo);
  };

  // Amostra de evolução (3 fotos: primeira, do meio e mais recente)
  const evo = pavSel?.evolucao || [];
  const amostra = evo.length <= 3
    ? evo
    : [evo[0], evo[Math.floor((evo.length - 1) / 2)], evo[evo.length - 1]];

  return (
    <>
      <div className="gd-torre__controles">
        <div className="gd-semana">
          <div className="gd-semana__topo">
            <button type="button" onClick={() => irSemana(-7)} aria-label="Semana anterior"><Icon name="chevron_left" /></button>
            <strong>{MESES[mesDia].toUpperCase()} {dia.slice(0, 4)}</strong>
            <button type="button" onClick={() => irSemana(7)} disabled={proxSemanaBloqueada} aria-label="Próxima semana"><Icon name="chevron_right" /></button>
            <button type="button" className="gd-dia-nav__hoje" onClick={() => setDia(hojeISO)} disabled={dia === hojeISO}>Hoje</button>
          </div>
          <div className="gd-semana__dias">
            {semana.map((iso) => (
              <button
                type="button"
                key={iso}
                className={`gd-semana__dia${iso === dia ? " gd-semana__dia--sel" : ""}${iso === hojeISO ? " gd-semana__dia--hoje" : ""}`}
                disabled={iso > hojeISO}
                onClick={() => setDia(iso)}
                aria-pressed={iso === dia}
                title={`${DIAS_LONGO[dowDe(iso)]}, ${fmtBR(iso)}`}
              >
                <span>{DIAS_CURTO[dowDe(iso)].toLowerCase()}</span>
                <strong>{Number(iso.slice(8))}</strong>
              </button>
            ))}
          </div>
        </div>
        {foco && <button type="button" className="gd-link gd-link--solto" onClick={() => setFoco(null)}>Limpar destaque</button>}
      </div>

      <div className="gd-torre-layout">
        <aside className="gd-emp-lateral">
          {legenda.length > 0 ? legenda.map((e) => (
            <button
              type="button" key={e.nome}
              className={`gd-emp-chip${foco === e.nome ? " gd-emp-chip--foco" : ""}`}
              style={{ "--c": e.cor }} aria-pressed={foco === e.nome}
              title={`${e.qtd} serviço${e.qtd > 1 ? "s" : ""} em ${e.locais} local${e.locais > 1 ? "is" : ""}`}
              onClick={() => onPickEmp(e.nome)}
            >
              <i className="gd-emp-chip__dot" />{e.nome}<b>{e.locais}</b>
            </button>
          )) : (
            <p className="gd-vazio">Nenhuma empresa informada neste dia.</p>
          )}
        </aside>

        {/* Coluna do carrossel de evolução (entre os badges e a torre) */}
        <div className="gd-evo-col">
          <h5>Evolução do pavimento</h5>
          {!pavSel ? (
            <small>Clique em um pavimento da torre.</small>
          ) : evo.length === 0 ? (
            <small>{pavSel.nome}: sem fotos no histórico.</small>
          ) : (
            <>
              <small>{pavSel.nome}</small>
              <div className="gd-evo-col__fotos">
                {amostra.map((f) => (
                  <button type="button" key={f.url} onClick={() => onFotos(evo, evo.indexOf(f))} title={`Abrir carrossel (${evo.length} fotos)`}>
                    <img src={f.url} alt={f.legenda || pavSel.nome} loading="lazy" />
                    <span>{fmtCurto(f.data)}</span>
                  </button>
                ))}
              </div>
              <button type="button" className="gd-evo-col__btn" onClick={() => onFotos(evo, 0)}>
                <Icon name="slideshow" size={16} /> Abrir carrossel ({evo.length})
              </button>
            </>
          )}
        </div>

        <div className="gd-torre">
          <div className="gd-torre__desenho"><TorreSvg pavs={pavs} locs={locs} foco={foco} onPick={onPick} onPickEmp={onPickEmp} onFotos={onFotos} onEvolucao={onEvolucao} /></div>
        </div>
        {(pavSel || empSel) && (
          <aside className="gd-torre-painel">
            <header className="gd-torre-painel__head">
              <div>
                <h4>{pavSel ? pavSel.nome : empSel.nome}</h4>
                <span>
                  {fmtBR(dia)} · {pavSel
                    ? `${pavSel.total} serviço${pavSel.total === 1 ? "" : "s"}`
                    : `${empSel.locais.reduce((a, l) => a + l.topicos.length, 0)} serviços em ${empSel.locais.length} local(is)`}
                </span>
              </div>
              <button type="button" onClick={onFechar} aria-label="Fechar"><Icon name="close" /></button>
            </header>
            <div className="gd-torre-painel__corpo">
              {pavSel
                ? <ConteudoTorrePav pav={pavSel} cores={cores} onFotos={onFotos} />
                : <ConteudoTorreEmp locais={empSel.locais} />}
            </div>
          </aside>
        )}
      </div>

      <div className="gd-legenda gd-legenda--centro">
        <span><i className="gd-legenda__cor" style={{ background: "#86efac" }} />Com atividade no dia</span>
        <span><i className="gd-legenda__cor" style={{ background: "#cbd5e1" }} />Sem atividade no dia</span>
      </div>
    </>
  );
}

/* ── Drawer genérico ─────────────────────────────────────────────── */
function Drawer({ titulo, subtitulo, onClose, onPrev, onNext, children }) {
  return (
    <div className="gd-overlay" onClick={onClose}>
      <aside className="gd-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={titulo}>
        <header className="gd-drawer__head">
          <div>
            <h2>{titulo}</h2>
            {subtitulo}
          </div>
          <div className="gd-drawer__nav">
            {onPrev && <button type="button" onClick={onPrev} aria-label="Dia anterior"><Icon name="chevron_left" /></button>}
            {onNext && <button type="button" onClick={onNext} aria-label="Próximo dia"><Icon name="chevron_right" /></button>}
            <button type="button" onClick={onClose} aria-label="Fechar"><Icon name="close" /></button>
          </div>
        </header>
        <div className="gd-drawer__corpo">{children}</div>
      </aside>
    </div>
  );
}

/* ── Lightbox de fotos ───────────────────────────────────────────── */
function Lightbox({ lista, index, setIndex, onClose }) {
  const foto = lista[index];
  const ir = useCallback((d) => setIndex((i) => (i + d + lista.length) % lista.length), [lista.length, setIndex]);

  useEffect(() => {
    const fn = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") ir(1);
      if (e.key === "ArrowLeft") ir(-1);
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [ir, onClose]);

  if (!foto) return null;
  return (
    <div className="gd-lightbox" onClick={onClose}>
      <button type="button" className="gd-lightbox__fechar" onClick={onClose} aria-label="Fechar"><Icon name="close" size={22} /></button>
      <figure onClick={(e) => e.stopPropagation()}>
        <img src={foto.url} alt={foto.legenda || "Foto da obra"} />
        <figcaption>
          {foto.legenda && <strong>{foto.legenda}</strong>}
          <span>{fmtBR(foto.data)}{foto.pav ? ` · ${foto.pav}` : ""} · {index + 1}/{lista.length}</span>
        </figcaption>
      </figure>
      {lista.length > 1 && (
        <>
          <button type="button" className="gd-lightbox__nav gd-lightbox__nav--prev" onClick={(e) => { e.stopPropagation(); ir(-1); }} aria-label="Foto anterior"><Icon name="chevron_left" size={28} /></button>
          <button type="button" className="gd-lightbox__nav gd-lightbox__nav--next" onClick={(e) => { e.stopPropagation(); ir(1); }} aria-label="Próxima foto"><Icon name="chevron_right" size={28} /></button>
        </>
      )}
    </div>
  );
}

/* ── Conteúdo do drawer de um dia ────────────────────────────────── */
function ConteudoDia({ iso, dados, abrirFotos, cores }) {
  const rdo = dados.porData[iso];
  if (!rdo) return <Vazio>Sem diário para este dia.</Vazio>;
  const ef = efetivoDia(rdo);
  const clima = dados.climaPorData[iso];
  const pavs = Object.keys(rdo.atividades || {}).filter((p) => txt(rdo.atividades[p]));
  const materiais = (rdo.recebimento_materiais || []).filter((m) => txt(m.descricao));
  const equips = (rdo.equipamentos || []).filter((e) => txt(e.descricao) || txt(e.codigo));
  const terc = (rdo.efetivo_terceirizado || []).filter((e) => txt(e.funcao));
  const fotosDia = dados.fotos.filter((f) => f.data === iso);

  return (
    <>
      <div className="gd-dd-grid">
        <div className="gd-dd-box">
          <span>Operação</span>
          <strong>{rdo.obra_op_manha === "sim" ? "Manhã ✓" : rdo.obra_op_manha === "nao" ? "Manhã ✕" : "Manhã —"}</strong>
          <strong>{rdo.obra_op_tarde === "sim" ? "Tarde ✓" : rdo.obra_op_tarde === "nao" ? "Tarde ✕" : "Tarde —"}</strong>
        </div>
        <div className="gd-dd-box">
          <span>Efetivo presente</span>
          <strong>{ef.presentes}<small> / {ef.total}</small></strong>
          <small>próprio {ef.pp}/{ef.pt} · terc. {ef.tp}/{ef.tt}</small>
        </div>
        <div className="gd-dd-box">
          <span>Clima</span>
          {clima?.itens?.length ? (
            clima.itens.map((c, i) => (
              <small key={i}>{c.periodo}: {c.condicao}{c.temperatura != null ? ` · ${Math.round(c.temperatura)}°C` : ""}</small>
            ))
          ) : (
            <small>{rdo.clima_manha || rdo.clima_tarde ? `${rdo.clima_manha || "—"} / ${rdo.clima_tarde || "—"}` : "Sem registro"}</small>
          )}
        </div>
      </div>

      {txt(rdo.responsavel_preenchimento) && (
        <p className="gd-dd-resp"><Icon name="edit_note" size={16} /> Preenchido por <strong>{rdo.responsavel_preenchimento}</strong></p>
      )}

      <h4 className="gd-dd-titulo">Atividades executadas</h4>
      {pavs.length === 0 ? <Vazio>Nenhuma atividade registrada.</Vazio> : pavs.map((pav) => (
        <div key={pav} className="gd-dd-pav">
          <div className="gd-dd-pav__nome">{pav}</div>
          <ul>
            {topicosDoDia(rdo, pav).map((t, i) => (
              <li key={i}>
                <span>{t.texto}</span>
                {t.empresa && <TagEmpresa nome={t.empresa} cores={cores} />}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {terc.length > 0 && (
        <>
          <h4 className="gd-dd-titulo">Terceirizados</h4>
          <ul className="gd-dd-lista">
            {terc.map((e, i) => <li key={i}><span>{e.funcao}</span><strong>{num(e.presentes)}/{num(e.total)}</strong></li>)}
          </ul>
        </>
      )}

      {materiais.length > 0 && (
        <>
          <h4 className="gd-dd-titulo">Materiais recebidos</h4>
          <ul className="gd-dd-lista">
            {materiais.map((m, i) => (
              <li key={i}><span>{m.descricao}{m.empresa ? ` · ${m.empresa}` : ""}</span><strong>{m.quantidade} {m.unidade}</strong></li>
            ))}
          </ul>
        </>
      )}

      {equips.length > 0 && (
        <>
          <h4 className="gd-dd-titulo">Equipamentos</h4>
          <ul className="gd-dd-lista">
            {equips.map((e, i) => <li key={i}><span>{e.descricao || e.codigo}</span><strong>{num(e.em_uso)}/{num(e.total)} em uso</strong></li>)}
          </ul>
        </>
      )}

      {rdo.tem_intercorrencias && txt(rdo.intercorrencias) && (
        <div className="gd-dd-interc">
          <strong><Icon name="warning" size={16} /> Intercorrência</strong>
          <p>{rdo.intercorrencias}</p>
        </div>
      )}

      {fotosDia.length > 0 && (
        <>
          <h4 className="gd-dd-titulo">Fotos ({fotosDia.length})</h4>
          <div className="gd-dd-fotos">
            {fotosDia.map((f, i) => (
              <button type="button" key={i} onClick={() => abrirFotos(fotosDia, i)}>
                <img src={f.url} alt={f.legenda || "Foto"} loading="lazy" />
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/* ── Conteúdo do drawer de um pavimento (torre do dia) ───────────── */
/* Só a foto grande do dia + a lista de serviços.
   A evolução do pavimento vive na coluna gd-evo-col (fora do painel). */
function ConteudoTorrePav({ pav, cores, onFotos }) {
  const fotos = pav.fotos || [];
  return (
    <>
      {fotos.length > 0 && (
        <div className="gd-pav-foto">
          <button type="button" onClick={() => onFotos(fotos, 0)} aria-label="Ampliar foto">
            <img src={fotos[0].url} alt={fotos[0].legenda || pav.nome} />
          </button>
          {fotos.length > 1 && <small>+{fotos.length - 1} foto{fotos.length > 2 ? "s" : ""} neste dia</small>}
        </div>
      )}
      {!pav.topicos.length ? <Vazio>Nenhum serviço registrado neste dia.</Vazio> : (
        <ul className="gd-dd-lista">
          {pav.topicos.map((t, i) => (
            <li key={i}>
              <span>{t.texto}</span>
              {t.empresa && <TagEmpresa nome={t.empresa} cores={cores} />}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/* ── Conteúdo do drawer de uma empresa (torre do dia) ────────────── */
function ConteudoTorreEmp({ locais }) {
  if (!locais.length) return <Vazio>Nenhum serviço desta empresa neste dia.</Vazio>;
  return locais.map((l) => (
    <div key={l.nome} className="gd-dd-pav">
      <div className="gd-dd-pav__nome">{l.nome}</div>
      <ul>{l.topicos.map((t, i) => <li key={i}>{t.texto}</li>)}</ul>
    </div>
  ));
}

/* ── Conteúdo do drawer histórico de um pavimento ────────────────── */
function ConteudoPav({ pav, cores, podeAbrir, abrirDia }) {
  if (!pav || pav.itens.length === 0) return <Vazio>Nenhuma atividade registrada nos Diários de Obra.</Vazio>;
  const emp = {};
  pav.itens.forEach((it) => it.topicos.forEach((t) => {
    if (!txt(t.empresa)) return;
    const k = norm(t.empresa);
    emp[k] = (emp[k] || 0) + 1;
  }));
  const lista = Object.entries(emp).sort((a, b) => b[1] - a[1]);

  return (
    <>
      {lista.length > 0 && (
        <div className="gd-pav-emps">
          <span>Empresas que atuaram:</span>
          {lista.map(([k, q]) => (
            <em key={k} className="gd-tag gd-tag--emp" style={{ "--c": cores[k] || COR_SEM_EMPRESA }}>{k} · {q}</em>
          ))}
        </div>
      )}
      <div className="gd-timeline gd-timeline--hist">
        {pav.itens.map((it) => {
          const corpo = (
            <>
              <span className="gd-timeline__data">{fmtBR(it.iso)}</span>
              <span className="gd-timeline__corpo">
                <ul>
                  {it.topicos.map((t, i) => (
                    <li key={i}>{t.texto}{t.empresa && <TagEmpresa nome={t.empresa} cores={cores} />}</li>
                  ))}
                </ul>
              </span>
            </>
          );
          return podeAbrir(it.iso) ? (
            <button type="button" key={it.iso} className="gd-timeline__item" onClick={() => abrirDia(it.iso)}>{corpo}</button>
          ) : (
            <div key={it.iso} className="gd-timeline__item gd-timeline__item--estatico" title="Mude para o mês deste dia para abrir o diário completo">{corpo}</div>
          );
        })}
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Componente principal
   ════════════════════════════════════════════════════════════════════ */
export default function CardsGraficos({ entity, entityType }) {
  const projectId = entity?.id;
  const agora = new Date();
  const [ano, setAno] = useState(agora.getFullYear());
  const [mes, setMes] = useState(agora.getMonth());

  const [rdos, setRdos] = useState([]);
  const [rdosAnt, setRdosAnt] = useState([]);
  const [clima, setClima] = useState([]);
  const [pavDb, setPavDb] = useState([]);
  const [projeto, setProjeto] = useState(null);
  const [loading, setLoading] = useState(true);

  const [painel, setPainel] = useState(null); // { tipo: "dia", iso } | { tipo: "pav", nome } | { tipo: "emp", nome }
  const [galeria, setGaleria] = useState(null); // { lista, index }
  const [verTodasFotos, setVerTodasFotos] = useState(false);

  const [historico, setHistorico] = useState([]); // todos os RDOs (colunas leves) p/ a torre
  const [empresaFoco, setEmpresaFoco] = useState(null);
  const [versao, setVersao] = useState(0); // incrementa quando um RDO é salvo
  const [fotosTorre, setFotosTorre] = useState({ dia: null, map: {} }); // fotos por pavimento do dia da torre

  const hojeISO = useMemo(() => {
    const d = new Date();
    return toISO(d.getFullYear(), d.getMonth(), d.getDate());
  }, []);

  const [diaTorre, setDiaTorre] = useState(hojeISO);

  /* ── Dados fixos do projeto ──────────────────────────────────── */
  useEffect(() => {
    if (!projectId || entityType !== "project") return;
    let cancel = false;
    (async () => {
      const [{ data: pav }, { data: proj }] = await Promise.all([
        supabase.from("pavimentos").select("*").eq("project_id", projectId).order("ordem", { ascending: true }),
        supabase.from("projects").select("name, data_inicio, data_finalizacao").eq("id", projectId).single(),
      ]);
      if (cancel) return;
      setPavDb(pav || []);
      setProjeto(proj || null);
    })();
    return () => { cancel = true; };
  }, [projectId, entityType]);

  /* ── Dados do mês (atual + anterior + clima) ─────────────────── */
  useEffect(() => {
    if (!projectId || entityType !== "project") return;
    let cancel = false;
    setLoading(true);
    const atual = mesRange(ano, mes);
    const ant = mes === 0 ? mesRange(ano - 1, 11) : mesRange(ano, mes - 1);

    (async () => {
      try {
        const [rAtual, rAnt, rClima] = await Promise.all([
          supabase.from("rdos").select("*").eq("project_id", projectId)
            .gte("data_rdo", atual.inicio).lte("data_rdo", atual.fim).order("data_rdo", { ascending: true }),
          supabase.from("rdos")
            .select("data_rdo, obra_op_manha, obra_op_tarde, efetivo_proprio, efetivo_terceirizado, tem_intercorrencias, intercorrencias, atividades, responsavel_preenchimento, feriado, dia_compensado")
            .eq("project_id", projectId).gte("data_rdo", ant.inicio).lte("data_rdo", ant.fim),
          supabase.from("clima_diario").select("data, periodo, condicao, temperatura, icone")
            .eq("project_id", projectId).gte("data", atual.inicio).lte("data", atual.fim),
        ]);
        if (cancel) return;
        setRdos(rAtual.data || []);
        setRdosAnt(rAnt.data || []);
        setClima(rClima.data || []);
      } catch (e) {
        console.error("Erro ao carregar painel do RDO:", e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [projectId, entityType, ano, mes, versao]);

  /* ── Histórico completo (última atividade e empresas por pavimento) ── */
  useEffect(() => {
    if (!projectId || entityType !== "project") return;
    let cancel = false;
    (async () => {
      const { data } = await supabase
        .from("rdos")
        .select("data_rdo, atividades, atividades_topicos, atividades_fotos")
        .eq("project_id", projectId)
        .order("data_rdo", { ascending: false });
      if (!cancel) setHistorico(data || []);
    })();
    return () => { cancel = true; };
  }, [projectId, entityType, versao]);

  /* ── Fotos por pavimento do dia selecionado na torre ─────────── */
  useEffect(() => {
    if (!projectId || entityType !== "project") return;
    let cancel = false;
    (async () => {
      const { data } = await supabase
        .from("rdos")
        .select("atividades_fotos")
        .eq("project_id", projectId)
        .eq("data_rdo", diaTorre);
      if (cancel) return;
      const map = {};
      (data || []).forEach((r) => {
        Object.entries(r.atividades_fotos || {}).forEach(([pav, lista]) => {
          const fs = (Array.isArray(lista) ? lista : []).filter((f) => f?.url);
          if (fs.length) map[pav] = [...(map[pav] || []), ...fs];
        });
      });
      setFotosTorre({ dia: diaTorre, map });
    })();
    return () => { cancel = true; };
  }, [projectId, entityType, diaTorre, versao]);

  // Recarrega quando um RDO é salvo em outro lugar da tela
  useEffect(() => {
    const fn = () => setVersao((v) => v + 1);
    window.addEventListener("rdoSalvo", fn);
    return () => window.removeEventListener("rdoSalvo", fn);
  }, []);

  const irMes = (delta) => {
    setPainel(null);
    const d = new Date(ano, mes + delta, 1);
    setAno(d.getFullYear());
    setMes(d.getMonth());
  };
  const irHoje = () => {
    setPainel(null);
    const d = new Date();
    setAno(d.getFullYear());
    setMes(d.getMonth());
  };

  /* ── Cálculo central ─────────────────────────────────────────── */
  const dados = useMemo(() => {
    const ultimo = new Date(ano, mes + 1, 0).getDate();
    const porData = {};
    rdos.forEach((r) => { if (r.data_rdo) porData[r.data_rdo] = r; });
    const validos = rdos.filter((r) => !especial(r) && preenchido(r));

    // clima por dia
    const climaPorData = {};
    const ordemPeriodo = { manha: 0, tarde: 1, noite: 2 };
    clima.forEach((c) => {
      const o = climaPorData[c.data] || (climaPorData[c.data] = { chuva: false, itens: [], temps: [] });
      o.itens.push(c);
      if (ehChuva(c)) o.chuva = true;
      if (c.temperatura != null) o.temps.push(Number(c.temperatura));
    });
    Object.values(climaPorData).forEach((o) => {
      o.itens.sort((a, b) => (ordemPeriodo[a.periodo] ?? 9) - (ordemPeriodo[b.periodo] ?? 9));
      o.temp = o.temps.length ? Math.round(o.temps.reduce((a, b) => a + b, 0) / o.temps.length) : null;
    });
    const choveu = (iso) => {
      const c = climaPorData[iso];
      if (c) return c.chuva;
      const r = porData[iso];
      return !!r && (r.clima_manha === "chuvoso" || r.clima_tarde === "chuvoso");
    };

    // dias do mês + status
    const dias = [];
    const esperados = [];
    for (let d = 1; d <= ultimo; d++) {
      const iso = toISO(ano, mes, d);
      const dow = dowDe(iso);
      const rdo = porData[iso];
      const futuro = iso > hojeISO;
      const fds = dow === 0 || dow === 6;
      let status;
      if (rdo && especial(rdo)) status = rdo.feriado ? "feriado" : "compensado";
      else if (preenchido(rdo)) status = statusOp(rdo);
      else if (futuro) status = "futuro";
      else if (fds) status = "fds";
      else if (iso === hojeISO) status = "hoje";
      else status = "pendente";

      const ef = rdo && preenchido(rdo) ? efetivoDia(rdo) : null;
      dias.push({
        d, iso, dow, rdo, status,
        chuva: choveu(iso),
        temp: climaPorData[iso]?.temp ?? null,
        interc: !!(rdo?.tem_intercorrencias && txt(rdo.intercorrencias)),
        ef,
        clicavel: !!rdo && (preenchido(rdo) || especial(rdo)),
      });
      if (!fds && !futuro && !(rdo && especial(rdo))) esperados.push({ iso, ok: preenchido(rdo) });
    }
    const base = esperados.filter((e) => e.iso !== hojeISO || e.ok);
    const preenchidosOk = base.filter((e) => e.ok).length;
    const pendencias = esperados.filter((e) => !e.ok && e.iso !== hojeISO).map((e) => e.iso);
    const taxaPreench = base.length ? pct(preenchidosOk, base.length) : 0;

    // efetivo
    const serieEf = dias.filter((x) => x.ef);
    const mediaDe = (key) => (serieEf.length ? Math.round(serieEf.reduce((a, x) => a + x.ef[key], 0) / serieEf.length) : 0);
    const efMedProprio = mediaDe("pp");
    const efMedTerc = mediaDe("tp");
    const efMaxTotal = Math.max(1, ...serieEf.map((x) => x.ef.pp + x.ef.tp));
    const efMedTotal = efMedProprio + efMedTerc;

    // próprio por função
    const proprioMap = {};
    validos.forEach((r) => (r.efetivo_proprio || []).forEach((e) => {
      const k = norm(e.funcao);
      if (!k) return;
      const o = proprioMap[k] || (proprioMap[k] = { nome: txt(e.funcao), pres: 0, tot: 0, dias: 0 });
      o.pres += num(e.presentes); o.tot += num(e.total); o.dias++;
    }));
    const proprioLista = Object.values(proprioMap)
      .map((o) => ({ ...o, presenca: o.tot > 0 ? pct(o.pres, o.tot) : null, media: o.dias ? +(o.pres / o.dias).toFixed(1) : 0 }))
      .sort((a, b) => b.pres - a.pres);

    // terceirizados
    const tercMap = {};
    validos.forEach((r) => {
      const dia = {};
      (r.efetivo_terceirizado || []).forEach((e) => {
        const k = norm(e.funcao);
        if (!k) return;
        const o = dia[k] || (dia[k] = { p: 0, t: 0 });
        o.p += num(e.presentes); o.t += num(e.total);
      });
      Object.entries(dia).forEach(([k, { p, t }]) => {
        const o = tercMap[k] || (tercMap[k] = { nome: k, presSoma: 0, totSoma: 0, dias: 0, pico: 0 });
        o.presSoma += p; o.totSoma += t; o.pico = Math.max(o.pico, p);
        if (p > 0) o.dias++;
      });
    });
    const tercLista = Object.values(tercMap)
      .map((o) => ({ ...o, media: o.dias ? Math.round(o.presSoma / o.dias) : 0, presenca: o.totSoma > 0 ? pct(o.presSoma, o.totSoma) : null }))
      .sort((a, b) => b.presSoma - a.presSoma);

    // atividades por empresa (tópicos)
    const empMap = {};
    validos.forEach((r) => Object.entries(r.atividades_topicos || {}).forEach(([pav, lista]) => {
      (Array.isArray(lista) ? lista : []).forEach((t) => {
        if (!txt(t.texto)) return;
        const k = t.empresa ? norm(t.empresa) : "SEM EMPRESA";
        const o = empMap[k] || (empMap[k] = { nome: k, qtd: 0, pavs: new Set(), dias: new Set() });
        o.qtd++; o.pavs.add(pav); o.dias.add(r.data_rdo);
      });
    }));
    const empLista = Object.values(empMap).sort((a, b) => b.qtd - a.qtd);

    // pavimentos
    const nomesDb = pavDb.map((p) => p.name);
    const pavMap = {};
    rdos.forEach((r) => Object.keys(r.atividades || {}).forEach((pav) => {
      if (!txt(r.atividades[pav])) return;
      (pavMap[pav] || (pavMap[pav] = [])).push({
        iso: r.data_rdo,
        topicos: topicosDoDia(r, pav),
        fotos: (r.atividades_fotos?.[pav] || []).length,
      });
    }));
    const pavLista = [
      ...nomesDb.filter((n) => pavMap[n]),
      ...Object.keys(pavMap).filter((n) => !nomesDb.includes(n)),
    ].map((nome) => ({ nome, itens: pavMap[nome], dias: new Set(pavMap[nome].map((i) => i.iso)) }));
    const pavSem = nomesDb.filter((n) => !pavMap[n]);

    // materiais
    const matMap = {};
    validos.forEach((r) => (r.recebimento_materiais || []).forEach((m) => {
      const k = txt(m.descricao);
      if (!k) return;
      const o = matMap[k] || (matMap[k] = { nome: k, un: m.unidade || "", qtd: 0, entregas: 0, empresas: new Set(), ultima: "" });
      o.qtd += num(m.quantidade); o.entregas++;
      if (txt(m.empresa)) o.empresas.add(txt(m.empresa));
      if (r.data_rdo > o.ultima) o.ultima = r.data_rdo;
    }));
    const matLista = Object.values(matMap).sort((a, b) => b.entregas - a.entregas || b.qtd - a.qtd);
    const totalEntregas = matLista.reduce((a, m) => a + m.entregas, 0);

    // equipamentos
    const eqMap = {};
    validos.forEach((r) => (r.equipamentos || []).forEach((e) => {
      const k = txt(e.descricao) || txt(e.codigo);
      if (!k) return;
      const o = eqMap[k] || (eqMap[k] = { nome: k, uso: 0, tot: 0, dias: 0, max: 0 });
      o.uso += num(e.em_uso); o.tot += num(e.total); o.dias++; o.max = Math.max(o.max, num(e.total));
    }));
    const eqLista = Object.values(eqMap)
      .map((o) => ({ ...o, util: o.tot > 0 ? pct(o.uso, o.tot) : null }))
      .sort((a, b) => (b.util ?? -1) - (a.util ?? -1));

    // intercorrências, responsáveis, fotos
    const interc = rdos.filter((r) => r.tem_intercorrencias && txt(r.intercorrencias));
    const respMap = {};
    validos.forEach((r) => { const k = txt(r.responsavel_preenchimento) || "Não informado"; respMap[k] = (respMap[k] || 0) + 1; });
    const respLista = Object.entries(respMap).sort((a, b) => b[1] - a[1]);

    const fotos = [];
    rdos.forEach((r) => {
      (Array.isArray(r.fotos) ? r.fotos : []).filter(Boolean).forEach((url) => fotos.push({ url, data: r.data_rdo, pav: null, legenda: "" }));
      Object.entries(r.atividades_fotos || {}).forEach(([pav, lista]) => {
        (Array.isArray(lista) ? lista : []).forEach((f) => { if (f?.url) fotos.push({ url: f.url, data: r.data_rdo, pav, legenda: f.legenda || "" }); });
      });
    });
    fotos.sort((a, b) => (a.data < b.data ? 1 : -1));

    // clima agregado
    const diasChuva = dias.filter((x) => x.chuva && (x.dow !== 0 && x.dow !== 6 || x.rdo)).length;
    const paradosChuva = dias.filter((x) => x.chuva && x.status === "parado").length;
    const temps = dias.map((x) => x.temp).filter((t) => t != null);
    const tempMedia = temps.length ? Math.round(temps.reduce((a, b) => a + b, 0) / temps.length) : null;

    // cronograma
    let crono = null;
    if (projeto?.data_inicio && projeto?.data_finalizacao) {
      const ini = new Date(`${projeto.data_inicio}T00:00:00`);
      const fim = new Date(`${projeto.data_finalizacao}T00:00:00`);
      const hoje = new Date(`${hojeISO}T00:00:00`);
      const total = Math.max(1, Math.round((fim - ini) / 86400000));
      const decorr = Math.min(total, Math.max(0, Math.round((hoje - ini) / 86400000)));
      const restantes = Math.round((fim - hoje) / 86400000);
      const atrasoRdo = [...rdos].reverse().find((r) => num(r.atraso_dias) > 0);
      crono = { pct: pct(decorr, total), restantes, atraso: atrasoRdo ? num(atrasoRdo.atraso_dias) : 0, ini: projeto.data_inicio, fim: projeto.data_finalizacao };
    }

    const resumo = resumir(rdos);
    const resumoAnt = (() => { const r = resumir(rdosAnt); return r.dias > 0 ? r : null; })();

    // destaques automáticos
    const insights = [];
    if (pendencias.length > 0) insights.push({ tom: "ruim", icone: "event_busy", texto: `${pendencias.length} dia${pendencias.length > 1 ? "s" : ""} útil${pendencias.length > 1 ? "eis" : ""} sem diário preenchido.` });
    else if (base.length > 0) insights.push({ tom: "bom", icone: "event_available", texto: "Todos os dias úteis do período estão preenchidos." });
    if (diasChuva > 0) insights.push({ tom: paradosChuva > 0 ? "ruim" : "neutro", icone: "rainy", texto: `${diasChuva} dia${diasChuva > 1 ? "s" : ""} com chuva${paradosChuva ? `, ${paradosChuva} com obra parada` : ""}.` });
    if (resumo.dias > 0 && resumo.absenteismo >= 10) insights.push({ tom: "ruim", icone: "person_off", texto: `Absenteísmo de ${resumo.absenteismo}% no efetivo contratado.` });
    const tercBaixa = tercLista.filter((t) => t.presenca != null && t.presenca < 65 && t.dias > 0);
    if (tercBaixa.length) insights.push({ tom: "ruim", icone: "groups", texto: `Presença baixa: ${tercBaixa.slice(0, 3).map((t) => `${t.nome} (${t.presenca}%)`).join(", ")}.` });
    if (pavLista.length) { const top = [...pavLista].sort((a, b) => b.dias.size - a.dias.size)[0]; insights.push({ tom: "neutro", icone: "apartment", texto: `Frente mais ativa: ${top.nome} (${top.dias.size} dia${top.dias.size > 1 ? "s" : ""}).` }); }
    const eqOciosos = eqLista.filter((e) => e.util != null && e.util < 30 && e.dias >= 2);
    if (eqOciosos.length) insights.push({ tom: "neutro", icone: "precision_manufacturing", texto: `Baixa utilização: ${eqOciosos.slice(0, 2).map((e) => e.nome).join(", ")}.` });
    if (interc.length) insights.push({ tom: "ruim", icone: "warning", texto: `${interc.length} intercorrência${interc.length > 1 ? "s" : ""} registrada${interc.length > 1 ? "s" : ""} no mês.` });

    return {
      ultimo, porData, validos, dias, climaPorData, pendencias, taxaPreench, baseDias: base.length, preenchidosOk,
      serieEf, efMedProprio, efMedTerc, efMedTotal, efMaxTotal,
      proprioLista, tercLista, empLista, pavLista, pavSem,
      matLista, totalEntregas, eqLista, interc, respLista, fotos,
      diasChuva, paradosChuva, tempMedia, crono, resumo, resumoAnt, insights,
    };
  }, [rdos, rdosAnt, clima, pavDb, projeto, ano, mes, hojeISO]);

  /* ── 3 fotos aleatórias do mês (sorteadas só quando o mês/qtd muda) ── */
  const amostraFotos = useMemo(() => {
    const a = [...dados.fotos];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a.slice(0, 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados.fotos.length, ano, mes]);

  /* ── Torre: histórico por pavimento, cores das empresas e dados por dia ── */
  const histPav = useMemo(() => {
    const m = {};
    historico.forEach((r) => {
      if (!r.data_rdo) return;
      Object.keys(r.atividades || {}).forEach((pav) => {
        if (!txt(r.atividades[pav])) return;
        const o = m[pav] || (m[pav] = { itens: [], ultima: "" });
        o.itens.push({ iso: r.data_rdo, topicos: topicosDoDia(r, pav) });
        if (r.data_rdo > o.ultima) o.ultima = r.data_rdo;
      });
    });
    Object.values(m).forEach((o) => o.itens.sort((a, b) => (a.iso < b.iso ? 1 : -1)));
    return m;
  }, [historico]);

  /* ── Histórico de fotos por pavimento (para evolução fotográfica) ── */
  const histFotos = useMemo(() => {
    const m = {};
    historico.forEach((r) => {
      Object.entries(r.atividades_fotos || {}).forEach(([pav, lista]) => {
        (Array.isArray(lista) ? lista : []).forEach((f) => {
          if (!f?.url) return;
          (m[pav] || (m[pav] = [])).push({ url: f.url, legenda: f.legenda || "", data: r.data_rdo, pav });
        });
      });
    });
    // da mais antiga para a mais recente = evolução
    Object.values(m).forEach((l) => l.sort((a, b) => (a.data < b.data ? -1 : a.data > b.data ? 1 : 0)));
    return m;
  }, [historico]);

  // Cada empresa mantém sempre a mesma cor (as mais frequentes pegam as primeiras cores)
  const coresEmpresa = useMemo(() => {
    const cont = {};
    Object.values(histPav).forEach((o) => o.itens.forEach((it) => it.topicos.forEach((t) => {
      if (!txt(t.empresa)) return;
      const k = norm(t.empresa);
      cont[k] = (cont[k] || 0) + 1;
    })));
    const m = {};
    Object.entries(cont).sort((a, b) => b[1] - a[1]).forEach(([k], i) => { m[k] = PALETA_EMPRESAS[i % PALETA_EMPRESAS.length]; });
    return m;
  }, [histPav]);

  const torre = useMemo(() => {
    const fotosDoDia = fotosTorre.dia === diaTorre ? fotosTorre.map : {};

    const montar = (p) => {
      const emp = {};
      const topicos = [];
      let sem = 0;
      (histPav[p.name]?.itens || []).forEach((it) => {
        if (it.iso !== diaTorre) return;
        it.topicos.forEach((t) => {
          topicos.push(t);
          if (!txt(t.empresa)) { sem++; return; }
          const k = norm(t.empresa);
          const e = emp[k] || (emp[k] = { nome: k, qtd: 0, cor: coresEmpresa[k] || COR_SEM_EMPRESA });
          e.qtd++;
        });
      });
      return {
        id: p.id, nome: p.name, total: topicos.length, semEmpresa: sem, topicos,
        cor: topicos.length > 0 ? "#86efac" : "#cbd5e1",
        empresas: Object.values(emp).sort((a, b) => b.qtd - a.qtd),
        fotos: (fotosDoDia[p.name] || []).map((f) => ({ url: f.url, legenda: f.legenda || "", data: diaTorre, pav: p.name })),
        evolucao: histFotos[p.name] || [],
      };
    };

    const todos = pavDb.filter((p) => txt(p.name));
    const pavs = todos.filter((p) => !p.tipo || p.tipo === "pavimento").map(montar).reverse();
    const locs = todos.filter((p) => p.tipo === "locacao").map(montar);

    const leg = {};
    [...pavs, ...locs].forEach((p) => p.empresas.forEach((e) => {
      const o = leg[e.nome] || (leg[e.nome] = { nome: e.nome, cor: e.cor, qtd: 0, locais: 0 });
      o.qtd += e.qtd; o.locais++;
    }));
    return { pavs, locs, legenda: Object.values(leg).sort((a, b) => b.locais - a.locais || b.qtd - a.qtd) };
  }, [histPav, histFotos, coresEmpresa, pavDb, diaTorre, fotosTorre]);

  const focoAtivo = torre.legenda.some((e) => e.nome === empresaFoco) ? empresaFoco : null;

  const abrirEmpresa = (nome) => {
    setEmpresaFoco(nome);
    setPainel({ tipo: "emp", nome });
  };

  /* ── Navegação entre dias no drawer ──────────────────────────── */
  const diasClicaveis = useMemo(() => dados.dias.filter((x) => x.clicavel).map((x) => x.iso), [dados.dias]);
  const abrirDia = useCallback((iso) => setPainel({ tipo: "dia", iso }), []);
  const vizinho = (iso, d) => {
    const i = diasClicaveis.indexOf(iso);
    return i === -1 ? null : diasClicaveis[i + d] || null;
  };
  const abrirFotos = (lista, index) => setGaleria({ lista, index });

  useEffect(() => {
    if (!painel || galeria) return;
    const fn = (e) => { if (e.key === "Escape") setPainel(null); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [painel, galeria]);

  /* ── Estados vazios ──────────────────────────────────────────── */
  if (entityType !== "project") {
    return <div className="gd-root"><Vazio>O painel do Diário de Obra está disponível apenas para projetos.</Vazio></div>;
  }

  const { resumo, resumoAnt, crono } = dados;
  const temDados = dados.validos.length > 0;
  const fotosVisiveis = verTodasFotos ? dados.fotos : dados.fotos.slice(0, 12);
  const painelDia = painel?.tipo === "dia" ? dados.dias.find((x) => x.iso === painel.iso) : null;
  const locaisTorre = [...torre.pavs, ...torre.locs];
  const painelPav = painel?.tipo === "pav" ? locaisTorre.find((p) => p.nome === painel.nome) : null;
  const painelEmp = painel?.tipo === "emp"
    ? {
        nome: painel.nome,
        locais: locaisTorre
          .map((p) => ({ nome: p.nome, topicos: p.topicos.filter((t) => norm(t.empresa) === painel.nome) }))
          .filter((l) => l.topicos.length),
      }
    : null;

  return (
    <div className="gd-root">
      {/* ── Barra superior ─────────────────────────────────────── */}
      <div className="gd-topo">
        <div className="gd-topo__titulo">
          <div>
            <h1>Painel do Diário de Obra</h1>
          </div>
        </div>
        <div className="gd-mes">
          <button type="button" onClick={() => irMes(-1)} aria-label="Mês anterior"><Icon name="chevron_left" /></button>
          <strong>{MESES[mes]} {ano}</strong>
          <button type="button" onClick={() => irMes(1)} aria-label="Próximo mês"><Icon name="chevron_right" /></button>
          <button type="button" className="gd-mes__hoje" onClick={irHoje}>Hoje</button>
        </div>
      </div>

      {loading ? (
        <div className="gd-skeleton">
          <div /><div /><div /><div />
        </div>
      ) : (
        <div className="gd-corpo">
          {/* ── Cronograma ──────────────────────────────────────── */}
          {crono && (
            <div className="gd-crono">
              <div className="gd-crono__info">
                <span>Cronograma da obra</span>
                <strong>{crono.pct}% do prazo decorrido</strong>
              </div>
              <div className="gd-crono__trilha" title={`${fmtBR(crono.ini)} → ${fmtBR(crono.fim)}`}>
                <div style={{ width: `${crono.pct}%` }} />
              </div>
              <div className="gd-crono__info gd-crono__info--dir">
                <span>Término previsto {fmtBR(crono.fim)}</span>
                <strong className={crono.atraso > 0 ? "gd-neg" : ""}>
                  {crono.restantes >= 0 ? `${crono.restantes} dias restantes` : `${Math.abs(crono.restantes)} dias além do prazo`}
                  {crono.atraso > 0 ? ` · atraso de ${crono.atraso}d` : ""}
                </strong>
              </div>
            </div>
          )}

          {/* ── Linha 1: preenchimento + KPIs ───────────────────── */}
          <div className="gd-linha gd-linha--hero">
            <div className="gd-hero">
              <div className="gd-ring" style={{ "--p": dados.taxaPreench }}>
                <div className="gd-ring__miolo">
                  <strong>{dados.taxaPreench}%</strong>
                  <span>preenchido</span>
                </div>
              </div>
              <div className="gd-hero__texto">
                <h3>Preenchimento do diário</h3>
                <p>{dados.preenchidosOk} de {dados.baseDias} dias úteis</p>
                {dados.pendencias.length > 0 ? (
                  <div className="gd-chips">
                    {dados.pendencias.slice(0, 8).map((iso) => (
                      <span key={iso} className="gd-chip gd-chip--pendente" title={`${DIAS_LONGO[dowDe(iso)]}, ${fmtBR(iso)}`}>{fmtCurto(iso)}</span>
                    ))}
                    {dados.pendencias.length > 8 && <span className="gd-chip">+{dados.pendencias.length - 8}</span>}
                  </div>
                ) : (
                  <p className="gd-ok">Nenhuma pendência no período.</p>
                )}
              </div>
            </div>

            <div className="gd-kpis">
              <Kpi icon="engineering" label="Dias operacionais"
                value={temDados ? `${resumo.op}/${resumo.dias}` : "—"}
                sub={temDados ? `${resumo.pctOp}% dos dias registrados` : "sem registros"}
                delta={temDados && <Delta atual={resumo.pctOp} anterior={resumoAnt?.pctOp} unidade=" pp" />} tom="verde" />
              <Kpi icon="groups" label="Efetivo médio por dia"
                value={temDados ? dados.efMedTotal : "—"}
                sub={temDados ? `${dados.efMedProprio} próprio · ${dados.efMedTerc} terceirizado` : "sem registros"}
                delta={temDados && <Delta atual={resumo.efetivoMedio} anterior={resumoAnt?.efetivoMedio} />} tom="azul" />
              <Kpi icon="person_off" label="Absenteísmo"
                value={temDados ? `${resumo.absenteismo}%` : "—"}
                sub="ausentes sobre o efetivo contratado"
                delta={temDados && <Delta atual={resumo.absenteismo} anterior={resumoAnt?.absenteismo} inverso unidade=" pp" />}
                tom={resumo.absenteismo >= 10 ? "vermelho" : "neutro"} />
              <Kpi icon="rainy" label="Dias de chuva"
                value={dados.diasChuva}
                sub={dados.paradosChuva ? `${dados.paradosChuva} com obra parada` : dados.tempMedia != null ? `média de ${dados.tempMedia}°C` : "sem impacto registrado"}
                tom="ciano" />
              <Kpi icon="warning" label="Intercorrências"
                value={dados.interc.length}
                sub={dados.interc.length ? "veja a lista abaixo" : "nenhuma no mês"}
                delta={temDados && <Delta atual={resumo.interc} anterior={resumoAnt?.interc} inverso />}
                tom={dados.interc.length ? "ambar" : "neutro"} />
              <Kpi icon="photo_camera" label="Fotos registradas"
                value={dados.fotos.length}
                sub={`${dados.totalEntregas} recebimento${dados.totalEntregas === 1 ? "" : "s"} de material`}
                tom="neutro"
                visual={amostraFotos.length > 0 ? (
                  <button
                    type="button"
                    className="gd-kpi__fotos"
                    onClick={() => abrirFotos(dados.fotos, Math.max(0, dados.fotos.indexOf(amostraFotos[0])))}
                    title="Abrir fotos do mês"
                    aria-label={`Abrir carrossel com ${dados.fotos.length} fotos do mês`}
                  >
                    {amostraFotos.map((f, i) => (
                      <img key={f.url} src={f.url} alt="" loading="lazy" style={{ "--i": i }} />
                    ))}
                  </button>
                ) : null} />
            </div>
          </div>

          {/* ── Linha 2: calendário + destaques ─────────────────── */}
          <div className="gd-linha gd-linha--2">
            <Card titulo="Calendário do mês" icone="calendar_month" badge={`${dados.validos.length} diários`}>
              <div className="gd-cal">
                {DIAS_CURTO.map((d) => <div key={d} className="gd-cal__cab">{d}</div>)}
                {Array.from({ length: dowDe(toISO(ano, mes, 1)) }).map((_, i) => <div key={`v${i}`} />)}
                {dados.dias.map((x) => {
                  const info = STATUS_INFO[x.status];
                  const titulo = `${DIAS_LONGO[x.dow]}, ${fmtBR(x.iso)} — ${info.label}${x.chuva ? " · chuva" : ""}${x.ef ? ` · ${x.ef.presentes} no efetivo` : ""}`;
                  return (
                    <button
                      type="button"
                      key={x.iso}
                      className={`gd-cal__dia gd-cal__dia--${info.cls}`}
                      title={titulo}
                      disabled={!x.clicavel}
                      onClick={() => abrirDia(x.iso)}
                    >
                      <span className="gd-cal__num">{x.d}</span>
                      <span className="gd-cal__marcas">
                        {x.chuva && <Icon name="water_drop" size={11} className="gd-cal__chuva" />}
                        {x.interc && <Icon name="warning" size={11} className="gd-cal__interc" />}
                      </span>
                      {x.ef && <span className="gd-cal__ef">{x.ef.presentes}</span>}
                    </button>
                  );
                })}
              </div>
              <div className="gd-legenda">
                {["op", "parcial", "parado", "feriado", "pendente"].map((k) => (
                  <span key={k}><i className={`gd-legenda__cor gd-legenda__cor--${k}`} />{STATUS_INFO[k].label}</span>
                ))}
                <span><Icon name="water_drop" size={12} className="gd-cal__chuva" /> Chuva</span>
                <span><Icon name="warning" size={12} className="gd-cal__interc" /> Intercorrência</span>
              </div>
            </Card>

            <Card titulo="Destaques do mês" icone="lightbulb">
              {dados.insights.length === 0 ? (
                <Vazio>Os destaques aparecem conforme os diários forem preenchidos.</Vazio>
              ) : (
                <ul className="gd-insights">
                  {dados.insights.map((it, i) => (
                    <li key={i} className={`gd-insight gd-insight--${it.tom}`}>
                      <Icon name={it.icone} size={18} />
                      <span>{it.texto}</span>
                    </li>
                  ))}
                </ul>
              )}
              {dados.respLista.length > 0 && (
                <div className="gd-legendai">
                  <h4 className="gd-sub">Quem preenche</h4>
                  <ul className="gd-ranking">
                    {dados.respLista.slice(0, 5).map(([nome, qtd]) => (
                      <li key={nome}>
                        <span>{nome}</span>
                        <Barra valor={qtd} max={dados.respLista[0][1]} tom="azul" />
                        <strong>{qtd}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          </div>

          {/* ── Torre da obra com empresas por pavimento ────────── */}
          <Card titulo="Torre da obra" icone="domain" badge={`${torre.legenda.length} empresa${torre.legenda.length === 1 ? "" : "s"} no dia`}>
            <TorreObra
              torre={torre}
              dia={diaTorre}
              setDia={setDiaTorre}
              hojeISO={hojeISO}
              foco={focoAtivo}
              setFoco={setEmpresaFoco}
              onPick={(nome) => setPainel({ tipo: "pav", nome })}
              onPickEmp={abrirEmpresa}
              onEvolucao={(nome) => setPainel({ tipo: "pav", nome })}
              onFotos={abrirFotos}
              pavSel={painelPav}
              empSel={painelEmp}
              onFechar={() => setPainel(null)}
              cores={coresEmpresa}
            />
          </Card>

          {!temDados ? (
            <Card titulo="Sem diários neste mês" icone="inbox">
              <Vazio>Nenhum Diário de Obra preenchido em {MESES[mes]} de {ano}. Use as setas para navegar entre os meses.</Vazio>
            </Card>
          ) : (
            <>
              {/* ── Efetivo por dia ─────────────────────────────── */}
              <Card titulo="Efetivo presente por dia" icone="groups" badge={`média ${dados.efMedTotal}/dia`}>
                <div className="gd-ef">
                  <div className="gd-ef__eixo"><span>{dados.efMaxTotal}</span><span>{Math.round(dados.efMaxTotal / 2)}</span><span>0</span></div>
                  <div className="gd-ef__area">
                    <div className="gd-ef__media" style={{ bottom: `${pct(dados.efMedTotal, dados.efMaxTotal)}%` }}><span>média</span></div>
                    {dados.dias.map((x) => {
                      const pp = x.ef?.pp || 0;
                      const tp = x.ef?.tp || 0;
                      return (
                        <button
                          type="button"
                          key={x.iso}
                          className={`gd-ef__col${x.dow === 0 || x.dow === 6 ? " gd-ef__col--fds" : ""}`}
                          disabled={!x.clicavel}
                          onClick={() => abrirDia(x.iso)}
                          title={x.ef ? `${fmtCurto(x.iso)} — ${pp} próprio + ${tp} terceirizado` : `${fmtCurto(x.iso)} — sem dados`}
                        >
                          <div className="gd-ef__pilha">
                            <div className="gd-ef__terc" style={{ height: `${pct(tp, dados.efMaxTotal)}%` }} />
                            <div className="gd-ef__prop" style={{ height: `${pct(pp, dados.efMaxTotal)}%` }} />
                          </div>
                          <span className="gd-ef__dia">{x.d}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="gd-legenda">
                  <span><i className="gd-legenda__cor gd-legenda__cor--prop" />Próprio</span>
                  <span><i className="gd-legenda__cor gd-legenda__cor--terc" />Terceirizado</span>
                </div>
              </Card>

              {/* ── Empresas ────────────────────────────────────── */}
              <div className="gd-linha gd-linha--2">
                <Card titulo="Empresas terceirizadas" icone="handshake" badge={dados.tercLista.length}>
                  {dados.tercLista.length === 0 ? <Vazio>Nenhuma empresa lançada no efetivo terceirizado.</Vazio> : (
                    <div className="gd-tabela">
                      <div className="gd-tabela__cab gd-tabela__cab--emp"><span>Empresa</span><span>Dias</span><span>Média</span><span>Pico</span><span>Presença</span></div>
                      {dados.tercLista.map((e) => (
                        <div key={e.nome} className="gd-tabela__lin gd-tabela__lin--emp">
                          <span className="gd-trunc" title={e.nome}><i className="gd-dot" style={{ background: coresEmpresa[e.nome] || COR_SEM_EMPRESA }} />{e.nome}</span>
                          <span>{e.dias}</span>
                          <span>{e.media}</span>
                          <span>{e.pico}</span>
                          <span className="gd-presenca">
                            <Barra valor={e.presenca ?? 0} max={100} tom={tomPresenca(e.presenca)} />
                            <b>{e.presenca == null ? "—" : `${e.presenca}%`}</b>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                <Card titulo="Serviços por empresa" icone="assignment_ind" badge={dados.empLista.length ? `${dados.empLista.reduce((a, e) => a + e.qtd, 0)} tópicos` : null}>
                  {dados.empLista.length === 0 ? (
                    <Vazio>Os serviços por empresa aparecem quando os tópicos de atividade são atribuídos a uma empresa.</Vazio>
                  ) : (
                    <div className="gd-tabela">
                      <div className="gd-tabela__cab gd-tabela__cab--srv"><span>Empresa</span><span>Serviços</span><span>Pavtos.</span><span>Dias</span></div>
                      {dados.empLista.map((e) => (
                        <div key={e.nome} className="gd-tabela__lin gd-tabela__lin--srv">
                          <span className="gd-trunc" title={e.nome}><i className="gd-dot" style={{ background: coresEmpresa[e.nome] || COR_SEM_EMPRESA }} />{e.nome === "SEM EMPRESA" ? "Sem empresa definida" : e.nome}</span>
                          <span className="gd-presenca"><Barra valor={e.qtd} max={dados.empLista[0].qtd} tom={e.nome === "SEM EMPRESA" ? "cinza" : "azul"} /><b>{e.qtd}</b></span>
                          <span>{e.pavs.size}</span>
                          <span>{e.dias.size}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              {/* ── Efetivo próprio + Equipamentos ──────────────── */}
              <div className="gd-linha gd-linha--2">
                <Card titulo="Efetivo próprio por função" icone="badge" badge={dados.proprioLista.length}>
                  {dados.proprioLista.length === 0 ? <Vazio>Nenhuma função lançada.</Vazio> : (
                    <div className="gd-tabela">
                      <div className="gd-tabela__cab gd-tabela__cab--func"><span>Função</span><span>Média/dia</span><span>Presença</span></div>
                      {dados.proprioLista.map((f) => (
                        <div key={f.nome} className="gd-tabela__lin gd-tabela__lin--func">
                          <span className="gd-trunc" title={f.nome}>{f.nome}</span>
                          <span>{f.media}</span>
                          <span className="gd-presenca">
                            <Barra valor={f.presenca ?? 0} max={100} tom={tomPresenca(f.presenca)} />
                            <b>{f.presenca == null ? "—" : `${f.presenca}%`}</b>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                <Card titulo="Utilização de equipamentos" icone="precision_manufacturing" badge={dados.eqLista.length}>
                  {dados.eqLista.length === 0 ? <Vazio>Nenhum equipamento lançado.</Vazio> : (
                    <div className="gd-tabela">
                      <div className="gd-tabela__cab gd-tabela__cab--func"><span>Equipamento</span><span>Dias</span><span>Em uso</span></div>
                      {dados.eqLista.map((e) => (
                        <div key={e.nome} className="gd-tabela__lin gd-tabela__lin--func">
                          <span className="gd-trunc" title={e.nome}>{e.nome}</span>
                          <span>{e.dias}</span>
                          <span className="gd-presenca">
                            <Barra valor={e.util ?? 0} max={100} tom={e.util == null ? "cinza" : e.util >= 60 ? "verde" : e.util >= 30 ? "ambar" : "vermelho"} />
                            <b>{e.util == null ? "—" : `${e.util}%`}</b>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              {/* ── Materiais + Intercorrências ─────────────────── */}
              <div className="gd-linha gd-linha--2">
                <Card titulo="Materiais recebidos" icone="inventory_2" badge={`${dados.matLista.length} iten${dados.matLista.length === 1 ? "" : "s"}`}>
                  {dados.matLista.length === 0 ? <Vazio>Nenhum recebimento registrado.</Vazio> : (
                    <div className="gd-tabela">
                      <div className="gd-tabela__cab gd-tabela__cab--mat"><span>Material</span><span>Qtd.</span><span>Entregas</span><span>Último</span></div>
                      {dados.matLista.slice(0, 10).map((m) => (
                        <div key={m.nome} className="gd-tabela__lin gd-tabela__lin--mat">
                          <span className="gd-trunc" title={`${m.nome}${m.empresas.size ? ` — ${[...m.empresas].join(", ")}` : ""}`}>{m.nome}</span>
                          <span><b>{+m.qtd.toFixed(2)}</b> {m.un}</span>
                          <span>{m.entregas}</span>
                          <span>{fmtCurto(m.ultima)}</span>
                        </div>
                      ))}
                      {dados.matLista.length > 10 && <p className="gd-mais">+ {dados.matLista.length - 10} itens</p>}
                    </div>
                  )}
                </Card>

                <Card titulo="Intercorrências" icone="warning" badge={dados.interc.length || "nenhuma"}>
                  {dados.interc.length === 0 ? <Vazio>Nenhuma intercorrência registrada neste mês.</Vazio> : (
                    <div className="gd-timeline">
                      {dados.interc.map((r) => (
                        <button type="button" key={r.id || r.data_rdo} className="gd-timeline__item gd-timeline__item--alerta" onClick={() => abrirDia(r.data_rdo)}>
                          <span className="gd-timeline__data">{fmtCurto(r.data_rdo)}</span>
                          <span className="gd-timeline__corpo"><p>{r.intercorrencias}</p></span>
                        </button>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              {/* ── Galeria ─────────────────────────────────────── */}
              <Card titulo="Registro fotográfico" icone="photo_library" badge={dados.fotos.length}>
                {dados.fotos.length === 0 ? <Vazio>Nenhuma foto anexada aos diários deste mês.</Vazio> : (
                  <>
                    <div className="gd-galeria">
                      {fotosVisiveis.map((f, i) => (
                        <button type="button" key={`${f.url}-${i}`} onClick={() => abrirFotos(dados.fotos, i)} title={f.legenda || f.pav || ""}>
                          <img src={f.url} alt={f.legenda || "Foto da obra"} loading="lazy" />
                          <span>{fmtCurto(f.data)}{f.pav ? ` · ${f.pav}` : ""}</span>
                        </button>
                      ))}
                    </div>
                    {dados.fotos.length > 12 && (
                      <button type="button" className="gd-link" onClick={() => setVerTodasFotos((v) => !v)}>
                        {verTodasFotos ? "Mostrar menos" : `Ver todas as ${dados.fotos.length} fotos`}
                      </button>
                    )}
                  </>
                )}
              </Card>
            </>
          )}
        </div>
      )}

      {/* ── Drawers ─────────────────────────────────────────────── */}
      {painelDia && (
        <Drawer
          titulo={`${DIAS_LONGO[painelDia.dow]}, ${fmtBR(painelDia.iso)}`}
          subtitulo={<span className={`gd-status gd-status--${STATUS_INFO[painelDia.status].cls}`}>{STATUS_INFO[painelDia.status].label}</span>}
          onClose={() => setPainel(null)}
          onPrev={vizinho(painelDia.iso, -1) ? () => abrirDia(vizinho(painelDia.iso, -1)) : null}
          onNext={vizinho(painelDia.iso, 1) ? () => abrirDia(vizinho(painelDia.iso, 1)) : null}
        >
          <ConteudoDia iso={painelDia.iso} dados={dados} abrirFotos={abrirFotos} cores={coresEmpresa} />
        </Drawer>
      )}

      {galeria && (
        <Lightbox
          lista={galeria.lista}
          index={galeria.index}
          setIndex={(fn) => setGaleria((g) => ({ ...g, index: typeof fn === "function" ? fn(g.index) : fn }))}
          onClose={() => setGaleria(null)}
        />
      )}
    </div>
  );
}